/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useState, useCallback } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
// eslint-disable-line import/named

import { EuiFormControlLayout, useEuiValidatableControl } from '../form';
import { getFormControlClassNameForIconCount } from '../form/form_control_layout/_num_icons';
import { useCombinedRefs } from '../../services';
import { EuiI18nConsumer } from '../context';
import { ReactDatePicker } from './react-datepicker';
export var euiDatePickerDefaultDateFormat = 'MM/DD/YYYY';
export var euiDatePickerDefaultTimeFormat = 'hh:mm A';

// EuiDatePicker only supports a subset of props from react-datepicker.
var unsupportedProps = [
// We don't want to show multiple months next to each other
'monthsShown',
// There is no need to show week numbers
'showWeekNumbers',
// Our css adapts to height, no need to fix it
'fixedHeight',
// We force the month / year selection UI. No need to configure it
'dropdownMode',
// Short month is unnecessary. Our UI has plenty of room for full months
'useShortMonthInDropdown',
// The today button is not needed. This should always be external to the calendar
'todayButton',
// We hide the time caption, so there is no need to overwrite its text
'timeCaption',
// We always want keyboard accessibility on
'disabledKeyboardNavigation',
// This is easy enough to do. It can conflict with isLoading state
'isClearable',
// There is no reason to launch the datepicker in its own modal. Can always build these ourselves
'withPortal',
// Causes Error: Cannot read property 'clone' of undefined
'showMonthYearDropdown',
// We overridde this with `popoverPlacement`
'popperPlacement'];
export var EuiDatePicker = ({
  adjustDateOnChange = true,
  calendarClassName,
  className,
  compressed,
  controlOnly,
  customInput,
  dateFormat = euiDatePickerDefaultDateFormat,
  dayClassName,
  disabled,
  excludeDates,
  filterDate,
  fullWidth = false,
  iconType,
  injectTimes,
  inline,
  inputRef,
  isInvalid,
  isLoading,
  locale,
  maxDate,
  maxTime,
  minDate,
  minTime,
  onChange,
  onClear,
  openToDate,
  placeholder,
  popperClassName,
  popoverPlacement = 'downLeft',
  readOnly,
  selected,
  shadow = true,
  shouldCloseOnSelect = true,
  showIcon = true,
  showTimeSelect = false,
  showTimeSelectOnly,
  timeFormat = euiDatePickerDefaultTimeFormat,
  utcOffset,
  ...rest
}) => {
  const classes = classNames('euiDatePicker', {
    'euiDatePicker--inline': inline,
    'euiDatePicker--shadow': inline && shadow
  });
  const numIconsClass = controlOnly ? false : getFormControlClassNameForIconCount({
    isInvalid,
    isLoading
  });
  const datePickerClasses = classNames('euiDatePicker', 'euiFieldText', numIconsClass, !inline && {
    'euiFieldText--fullWidth': fullWidth,
    'euiFieldText-isLoading': isLoading,
    'euiFieldText--compressed': compressed,
    'euiFieldText--withIcon': showIcon,
    'euiFieldText--isClearable': selected && onClear
  }, className);
  let optionalIcon;
  if (inline || customInput || !showIcon) {
    optionalIcon = undefined;
  } else if (iconType) {
    optionalIcon = iconType;
  } else if (showTimeSelectOnly) {
    optionalIcon = 'clock';
  } else {
    optionalIcon = 'calendar';
  }

  // In case the consumer did not alter the default date format but wants
  // to add the time select, we append the default time format
  let fullDateFormat = dateFormat;
  if (showTimeSelect && dateFormat === euiDatePickerDefaultDateFormat) {
    fullDateFormat = `${dateFormat} ${timeFormat}`;
  }

  // Set an internal ref on ReactDatePicker's `input` so we can set its :invalid state via useEuiValidatableControl
  const [inputValidityRef, _setInputValidityRef] = useState(null);
  const setInputValidityRef = useCallback(ref => {
    _setInputValidityRef(ref?.input || null);
  }, []);
  useEuiValidatableControl({
    isInvalid,
    controlEl: inputValidityRef
  });
  const inputRefs = useCombinedRefs([inputRef, setInputValidityRef]);
  const control = <EuiI18nConsumer>
      {({
      locale: contextLocale
    }) => {
      return <ReactDatePicker adjustDateOnChange={adjustDateOnChange} calendarClassName={calendarClassName} className={datePickerClasses} customInput={customInput} dateFormat={fullDateFormat} dayClassName={dayClassName} disabled={disabled} readOnly={readOnly} excludeDates={excludeDates} filterDate={filterDate} injectTimes={injectTimes} inline={inline} locale={locale || contextLocale} maxDate={maxDate} maxTime={maxTime} minDate={minDate} minTime={minTime} onChange={onChange} openToDate={openToDate} placeholderText={placeholder} popperClassName={popperClassName} ref={inputRefs} selected={selected} shouldCloseOnSelect={shouldCloseOnSelect} showMonthDropdown showTimeSelect={showTimeSelectOnly ? true : showTimeSelect} showTimeSelectOnly={showTimeSelectOnly} showYearDropdown timeFormat={timeFormat} utcOffset={utcOffset} yearDropdownItemNumber={7} accessibleMode={!(disabled || readOnly)} popperPlacement={popoverPlacement} {...rest} />;
    }}
    </EuiI18nConsumer>;
  if (controlOnly) return control;
  return <span className={classes}>
      <EuiFormControlLayout icon={optionalIcon} fullWidth={!inline && fullWidth} compressed={!inline && compressed} clear={selected && onClear ? {
      onClick: onClear
    } : undefined} isLoading={isLoading} isInvalid={isInvalid} isDisabled={disabled} readOnly={readOnly} className={classNames({
      // Take advantage of `euiFormControlLayoutDelimited`'s replacement input styling
      euiFormControlLayoutDelimited: inline,
      'euiFormControlLayoutDelimited--isInvalid': inline && isInvalid && !disabled && !readOnly
    })} iconsPosition={inline ? 'static' : undefined}>
        {control}
      </EuiFormControlLayout>
    </span>;
};
EuiDatePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Applies classes to the numbered days provided. Check docs for example.
     */
  dayClassName: PropTypes.func,
  /**
     * Renders the input as full width
     */
  fullWidth: PropTypes.bool,
  /**
     * Renders the input with compressed height and sizing
     */
  compressed: PropTypes.bool,
  /**
     * ref for the ReactDatePicker instance
     */
  inputRef: PropTypes.any,
  /**
     * Provides styling to the input when invalid
     */
  isInvalid: PropTypes.bool,
  /**
     * Provides styling to the input when loading
     */
  isLoading: PropTypes.bool,
  /**
     * What to do when the input is cleared by the x icon
     */
  onClear: PropTypes.func,
  /**
     * Opens to this date (in moment format) on first press, regardless of selection
     */
  openToDate: PropTypes.any,
  /**
     * Shows only when no date is selected
     */
  placeholder: PropTypes.string,
  /**
     * Displays the date picker calendar on directly on the page.
     * Will not render `iconType` or `fullWidth`.
     */
  inline: PropTypes.bool,
  /**
     * Allows turning the shadow off if using the `inline` prop
     */
  shadow: PropTypes.bool,
  /**
     * Show the icon in input
     */
  showIcon: PropTypes.bool,
  /**
     * Pass an icon type to change the default `calendar` or `clock` icon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired, PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
    side: PropTypes.any,
    color: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired]),
    ref: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired])
  }).isRequired]),
  /**
     * Sets the placement of the popover.
     *
     * **Use [EuiPopover](/#/layout/popover) values**: 'upCenter', 'upLeft', 'upRight', downCenter', 'downLeft', 'downRight', 'leftCenter', 'leftUp', 'leftDown', 'rightCenter', 'rightUp', 'rightDown'.
     */
  popoverPlacement: PropTypes.any,
  /**
     * Completely removes form control layout wrapper and ignores
     * iconType. Best used inside EuiFormControlLayoutDelimited.
     */
  controlOnly: PropTypes.bool
};
try {
  EuiDatePicker.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/date_picker.tsx',
    description: '',
    displayName: 'EuiDatePicker',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: 'Added to the actual input of the calendar',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      dayClassName: {
        defaultValue: null,
        description: 'Applies classes to the numbered days provided. Check docs for example.',
        name: 'dayClassName',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => string'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Renders the input as full width',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: 'Renders the input with compressed height and sizing',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      inputRef: {
        defaultValue: null,
        description: 'ref for the ReactDatePicker instance',
        name: 'inputRef',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Ref<Component<ReactDatePickerProps, any, any>>'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: 'Provides styling to the input when invalid',
        name: 'isInvalid',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Provides styling to the input when loading',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClear: {
        defaultValue: null,
        description: 'What to do when the input is cleared by the x icon',
        name: 'onClear',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      openToDate: {
        defaultValue: null,
        description: 'Opens to this date (in moment format) on first press, regardless of selection',
        name: 'openToDate',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Shows only when no date is selected',
        name: 'placeholder',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      inline: {
        defaultValue: null,
        description: 'Displays the date picker calendar on directly on the page.\n' + 'Will not render `iconType` or `fullWidth`.',
        name: 'inline',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shadow: {
        defaultValue: {
          value: 'true'
        },
        description: 'Allows turning the shadow off if using the `inline` prop',
        name: 'shadow',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showIcon: {
        defaultValue: {
          value: 'true'
        },
        description: 'Show the icon in input',
        name: 'showIcon',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Pass an icon type to change the default `calendar` or `clock` icon',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'IconType | IconShape'
        }
      },
      popoverPlacement: {
        defaultValue: {
          value: 'downLeft'
        },
        description: 'Sets the placement of the popover.\n' + '\n' + "**Use [EuiPopover](/#/layout/popover) values**: 'upCenter', 'upLeft', 'upRight', downCenter', 'downLeft', 'downRight', 'leftCenter', 'leftUp', 'leftDown', 'rightCenter', 'rightUp', 'rightDown'.",
        name: 'popoverPlacement',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"upCenter" | "upLeft" | "upRight" | "downCenter" | "downLeft" | "downRight" | "leftCenter" | "leftUp" | "leftDown" | "rightCenter" | "rightUp" | "rightDown"',
          value: [{
            value: '"upCenter"'
          }, {
            value: '"upLeft"'
          }, {
            value: '"upRight"'
          }, {
            value: '"downCenter"'
          }, {
            value: '"downLeft"'
          }, {
            value: '"downRight"'
          }, {
            value: '"leftCenter"'
          }, {
            value: '"leftUp"'
          }, {
            value: '"leftDown"'
          }, {
            value: '"rightCenter"'
          }, {
            value: '"rightUp"'
          }, {
            value: '"rightDown"'
          }]
        }
      },
      controlOnly: {
        defaultValue: null,
        description: 'Completely removes form control layout wrapper and ignores\n' + 'iconType. Best used inside EuiFormControlLayoutDelimited.',
        name: 'controlOnly',
        parent: {
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: '',
        name: 'autoFocus',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      tabIndex: {
        defaultValue: null,
        description: '',
        name: 'tabIndex',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'What to do when the input changes',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: KeyboardEvent<HTMLDivElement>) => void'
        }
      },
      onSelect: {
        defaultValue: null,
        description: '',
        name: 'onSelect',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      autoComplete: {
        defaultValue: null,
        description: '',
        name: 'autoComplete',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      required: {
        defaultValue: null,
        description: '',
        name: 'required',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClickOutside: {
        defaultValue: null,
        description: '',
        name: 'onClickOutside',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: MouseEvent<HTMLDivElement, MouseEvent>) => void'
        }
      },
      adjustDateOnChange: {
        defaultValue: {
          value: 'true'
        },
        description: 'Whether changes to Year and Month (via dropdowns) should trigger `onChange`',
        name: 'adjustDateOnChange',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      accessibleMode: {
        defaultValue: null,
        description: '',
        name: 'accessibleMode',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      allowSameDay: {
        defaultValue: null,
        description: '',
        name: 'allowSameDay',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      calendarClassName: {
        defaultValue: null,
        description: 'Optional class added to the calendar portion of datepicker',
        name: 'calendarClassName',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      customInput: {
        defaultValue: null,
        description: 'Replaces the input with any node, like a button',
        name: 'customInput',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      customInputRef: {
        defaultValue: null,
        description: '',
        name: 'customInputRef',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      dateFormat: {
        defaultValue: {
          value: 'MM/DD/YYYY'
        },
        description: 'Accepts any moment format string',
        name: 'dateFormat',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string | string[]'
        }
      },
      dateFormatCalendar: {
        defaultValue: null,
        description: '',
        name: 'dateFormatCalendar',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      endDate: {
        defaultValue: null,
        description: '',
        name: 'endDate',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      excludeDates: {
        defaultValue: null,
        description: '',
        name: 'excludeDates',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      excludeTimes: {
        defaultValue: null,
        description: '',
        name: 'excludeTimes',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      filterDate: {
        defaultValue: null,
        description: '',
        name: 'filterDate',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => boolean'
        }
      },
      forceShowMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'forceShowMonthNavigation',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      formatWeekNumber: {
        defaultValue: null,
        description: '',
        name: 'formatWeekNumber',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => string | number'
        }
      },
      highlightDates: {
        defaultValue: null,
        description: '',
        name: 'highlightDates',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      includeDates: {
        defaultValue: null,
        description: '',
        name: 'includeDates',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      includeTimes: {
        defaultValue: null,
        description: '',
        name: 'includeTimes',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      injectTimes: {
        defaultValue: null,
        description: 'Adds additional times to the time selector other then :30 increments',
        name: 'injectTimes',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      locale: {
        defaultValue: null,
        description: 'Switches the locale / display. "en-us", "zn-ch"...etc',
        name: 'locale',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      maxDate: {
        defaultValue: null,
        description: 'The max date accepted (in moment format) as a selection',
        name: 'maxDate',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      maxTime: {
        defaultValue: null,
        description: 'The max time accepted (in moment format) as a selection',
        name: 'maxTime',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minDate: {
        defaultValue: null,
        description: 'The min date accepted (in moment format) as a selection',
        name: 'minDate',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minTime: {
        defaultValue: null,
        description: 'The min time accepted (in moment format) as a selection',
        name: 'minTime',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      onChangeRaw: {
        defaultValue: null,
        description: '',
        name: 'onChangeRaw',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onMonthChange: {
        defaultValue: null,
        description: '',
        name: 'onMonthChange',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      onWeekSelect: {
        defaultValue: null,
        description: '',
        name: 'onWeekSelect',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(firstDayOfWeek: Moment, weekNumber: string | number, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onYearChange: {
        defaultValue: null,
        description: '',
        name: 'onYearChange',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      peekNextMonth: {
        defaultValue: null,
        description: '',
        name: 'peekNextMonth',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      placeholderText: {
        defaultValue: null,
        description: '',
        name: 'placeholderText',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      popperClassName: {
        defaultValue: null,
        description: 'Class applied to the popup, when inline is false',
        name: 'popperClassName',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      popperContainer: {
        defaultValue: null,
        description: '',
        name: 'popperContainer',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(props: { children: ReactNode[]; }) => ReactNode'
        }
      },
      preventOpenOnFocus: {
        defaultValue: null,
        description: '',
        name: 'preventOpenOnFocus',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableMonthYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableMonthYearDropdown',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableYearDropdown',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selected: {
        defaultValue: null,
        description: 'The selected datetime (in moment format)',
        name: 'selected',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      selectsEnd: {
        defaultValue: null,
        description: '',
        name: 'selectsEnd',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selectsStart: {
        defaultValue: null,
        description: '',
        name: 'selectsStart',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shouldCloseOnSelect: {
        defaultValue: {
          value: 'true'
        },
        description: 'Will close the popup on selection',
        name: 'shouldCloseOnSelect',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showDisabledMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'showDisabledMonthNavigation',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showMonthDropdown: {
        defaultValue: null,
        description: '',
        name: 'showMonthDropdown',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelect: {
        defaultValue: {
          value: 'false'
        },
        description: 'Show the time selection alongside the calendar',
        name: 'showTimeSelect',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelectOnly: {
        defaultValue: null,
        description: 'Only show the time selector, not the calendar',
        name: 'showTimeSelectOnly',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'showYearDropdown',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      startDate: {
        defaultValue: null,
        description: '',
        name: 'startDate',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      startOpen: {
        defaultValue: null,
        description: '',
        name: 'startOpen',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      strictParsing: {
        defaultValue: null,
        description: 'Use Moment strict mode, allowing exact format matches only',
        name: 'strictParsing',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      timeFormat: {
        defaultValue: {
          value: 'hh:mm A'
        },
        description: 'The format of the time within the selector, in moment notation',
        name: 'timeFormat',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      timeIntervals: {
        defaultValue: null,
        description: '',
        name: 'timeIntervals',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      useWeekdaysShort: {
        defaultValue: null,
        description: '',
        name: 'useWeekdaysShort',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      utcOffset: {
        defaultValue: null,
        description: '',
        name: 'utcOffset',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      weekLabel: {
        defaultValue: null,
        description: '',
        name: 'weekLabel',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      yearDropdownItemNumber: {
        defaultValue: null,
        description: '',
        name: 'yearDropdownItemNumber',
        parent: {
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/react-datepicker/src/index.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'EuiExtendedDatePickerProps', 'ReactDatePickerProps']
  };
} catch (e) {}